import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, Rocket, GitBranch, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const quickCommands = [
  'Deploy main to production',
  'Rollback last deployment',
  'Show deployment status',
  'Analyze my codebase',
  'What\'s the current traffic?',
  'Schedule deployment for tonight'
];

export default function AIChatPage() {
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm DeployForge AI, your intelligent deployment assistant. I can help you deploy code, analyze performance, detect errors, and more. Try commands like 'Deploy main to staging' or ask me anything about your infrastructure.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/ai/chat`, {
        message: input,
        context: { timestamp: new Date().toISOString() }
      }, {
        headers: getAuthHeaders()
      });

      const aiResponse = response.data;
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: aiResponse.response || aiResponse.raw_response || "I processed your request.",
        intent: aiResponse.intent,
        action: aiResponse.action,
        suggestions: aiResponse.suggestions,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I encountered an error processing your request. Please try again.",
        timestamp: new Date()
      }]);
      toast.error('Failed to get AI response');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickCommand = (command) => {
    setInput(command);
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex flex-col">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3" data-testid="ai-chat-title">
            <Sparkles className="w-8 h-8 text-primary" />
            AI Assistant
          </h1>
          <p className="text-foreground/60 mt-1">
            Natural language deployment commands and intelligent assistance
          </p>
        </motion.div>

        {/* Chat Container */}
        <Card className="flex-1 bg-card/50 border-border flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <AnimatePresence>
              {messages.map((message, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${
                    message.role === 'user' 
                      ? 'bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30' 
                      : 'bg-card border border-border'
                  } rounded-lg p-4`}>
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-xs text-primary font-medium">DeployForge AI</span>
                      </div>
                    )}
                    
                    <p className="text-foreground/90 whitespace-pre-wrap">{message.content}</p>
                    
                    {message.intent && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="px-2 py-1 bg-primary/10 rounded text-primary capitalize">
                            {message.intent}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {message.action && message.action.type && (
                      <div className="mt-2 p-3 bg-[#0A0E27] rounded border border-border">
                        <div className="flex items-center gap-2 text-sm">
                          {message.action.type === 'deploy' && <Rocket className="w-4 h-4 text-accent" />}
                          {message.action.type === 'rollback' && <AlertCircle className="w-4 h-4 text-secondary" />}
                          <span className="text-foreground/80 capitalize">{message.action.type}</span>
                          {message.action.branch && (
                            <span className="text-primary font-code text-xs">{message.action.branch}</span>
                          )}
                          {message.action.environment && (
                            <>
                              <span className="text-muted-foreground">→</span>
                              <span className="text-secondary">{message.action.environment}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {message.suggestions.slice(0, 3).map((suggestion, j) => (
                          <button
                            key={j}
                            onClick={() => handleQuickCommand(suggestion)}
                            className="text-xs px-2 py-1 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                    </div>
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Commands */}
          <div className="px-6 py-3 border-t border-border">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quickCommands.map((cmd, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickCommand(cmd)}
                  className="px-3 py-1.5 text-xs bg-card border border-border rounded-full text-foreground/70 hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                >
                  {cmd}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border bg-[#0A0E27]">
            <div className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a deployment command or ask a question..."
                className="flex-1 bg-card border-border focus:border-primary"
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="bg-primary text-black font-bold hover:shadow-neon-cyan"
                data-testid="ai-chat-send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
